import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode, Ref } from 'react';
import { Box, Button, Checkbox, Grid, Icon, Input, Stack } from '@ff-it/ui';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { useDraggable, useDragMonitor } from 'components/DND';
import { BuilderWorkspace, type BuilderWorkspaceProps } from 'components/BuilderWorkspace';
import { builderColumns } from 'components/BuilderWorkspace/BuilderWorkspace';
import { sidebar as workspaceSidebar } from 'components/BuilderWorkspace/BuilderWorkspace.css';
import { columnLetter, dropColumn } from './model';
import type { ExportColumn, ExportFieldGroup, ExportField } from './types';
import * as styles from './ExportBuilder.css';

export type ExportBuilderProps = {
  groups: ExportFieldGroup[];
  columns: ExportColumn[];
  onChange: (columns: ExportColumn[]) => void;
  disabled?: boolean;
  searchRef?: Ref<HTMLInputElement>;
  searchActions?: ReactNode;
  selectedHeader?: ReactNode;
  workspace?: Pick<BuilderWorkspaceProps, 'close' | 'actions' | 'footerLabel'>;
};

type ColumnRowProps = {
  column: ExportColumn;
  field: ExportField;
  groupTitle: string;
  index: number;
  total: number;
  instanceId: string;
  disabled: boolean;
  onRename: (title: string) => void;
  onRemove: () => void;
};

function ColumnRow({
  column,
  field,
  groupTitle,
  index,
  total,
  instanceId,
  disabled,
  onRename,
  onRemove,
}: ColumnRowProps): JSX.Element {
  const { elementRef, handleRef, isDragging, closestEdge } = useDraggable(index, disabled || total < 2, instanceId);
  const errorId = useId();
  const invalidTitle = !column.title.trim();
  return (
    <li
      ref={elementRef}
      className={styles.row}
      data-dragging={isDragging}
      data-field={field.id}
      aria-label={`Column ${columnLetter(index)}: ${field.title}`}
    >
      {closestEdge && <DropIndicator edge={closestEdge} />}
      <Button
        ref={handleRef}
        type="button"
        variant="ghost"
        size="xs"
        icon="grip-vertical"
        className={styles.dragHandle}
        aria-label={`Reorder ${field.title}`}
        title="Drag to reorder"
        disabled={disabled || total < 2}
      />
      <Box
        textSize="sm"
        className={styles.source}
        title={`${groupTitle} · ${field.title}${field.help_text ? ` — ${field.help_text}` : ''}`}
      >
        <Box as="span" color="secondary" textSize="xs">
          {groupTitle} ·{' '}
        </Box>
        {field.title}
      </Box>
      <Box as="span" textSize="xs" color="secondary" textAlign="center" className={styles.order}>
        {columnLetter(index)}
      </Box>
      <div className={styles.titleInput}>
        <Input
          size="sm"
          aria-label={`Title for ${field.title}`}
          aria-invalid={invalidTitle || undefined}
          aria-describedby={invalidTitle ? errorId : undefined}
          valid={invalidTitle ? false : undefined}
          value={column.title}
          disabled={disabled}
          onChange={(event) => onRename(event.target.value)}
        />
        {invalidTitle && (
          <Box as="span" id={errorId} color="danger" textSize="xs">
            Enter a column title.
          </Box>
        )}
      </div>
      <Box display="flex" className={styles.actions}>
        <Button
          type="button"
          size="xxs"
          variant="ghost"
          icon={field.required ? 'lock' : 'xmark'}
          aria-label={`Remove ${field.title}`}
          title={field.required ? 'Required field' : 'Remove column'}
          disabled={disabled || field.required}
          onClick={onRemove}
        />
      </Box>
    </li>
  );
}

export function ExportBuilder({
  groups,
  columns,
  onChange,
  disabled = false,
  searchRef,
  searchActions,
  selectedHeader,
  workspace,
}: ExportBuilderProps): JSX.Element {
  const instanceId = useId();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');
  // The shared monitor registers once. Read the latest controlled value when a
  // drop finishes, including title edits made after the initial render.
  const current = useRef({ columns, onChange });
  useEffect(() => {
    current.current = { columns, onChange };
  }, [columns, onChange]);
  useEffect(() => {
    if (disabled || !scrollRef.current) return;
    return autoScrollForElements({
      element: scrollRef.current,
      getAllowedAxis: () => 'vertical',
      canScroll: ({ source }) => source.data.instanceId === instanceId,
    });
  }, [instanceId, disabled]);
  useDragMonitor(
    (source, target, edge) => {
      const { columns: value, onChange: change } = current.current;
      const next = dropColumn(value, source.index, target.index, edge);
      if (next === value) return;
      change(next);
    },
    instanceId,
    disabled,
  );

  const definitions = new Map(
    groups.flatMap((group) => group.columns.map((field) => [field.id, { field, groupTitle: group.title }] as const)),
  );
  const selected = new Set(columns.map((column) => column.field));
  const query = search.trim().toLowerCase();
  const visibleGroups = groups
    .map((group) => ({
      ...group,
      columns: group.title.toLowerCase().includes(query)
        ? group.columns
        : group.columns.filter((field) => `${field.title} ${field.help_text ?? ''}`.toLowerCase().includes(query)),
    }))
    .filter((group) => group.columns.length > 0);

  function add(field: ExportField): void {
    if (disabled || selected.has(field.id)) return;
    onChange([...columns, { field: field.id, title: field.title }]);
  }
  function remove(field: ExportField): void {
    if (disabled || field.required) return;
    onChange(columns.filter((column) => column.field !== field.id));
  }

  const available = (
    <Box
      as="section"
      className={workspace ? workspaceSidebar : undefined}
      background={workspace ? 'secondary' : undefined}
      display="flex"
      flexDirection="column"
      minWidth="0"
      minHeight="0"
      aria-label="Available fields"
    >
      <Box p={workspace ? '3' : '0'} pb="2" flexShrink={0}>
        <Box display="flex" alignItems="center" gap="2">
          <Box flex="auto" minWidth="0">
            <Input
              ref={searchRef}
              type="search"
              size="sm"
              aria-label="Search fields"
              placeholder="Find a field or group…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Box>
          {searchActions}
        </Box>
      </Box>
      <div className={workspace ? `${styles.scrollViewport} ${styles.availableViewport}` : styles.scrollViewport}>
        <Stack space="0">
          {visibleGroups.map((group) => (
            <Box minWidth="0" key={group.id} role="group" aria-labelledby={`${instanceId}-${group.id}`}>
              <Box px="2" pt="2" pb="1" borderTop="subtle">
                <Box
                  as="h3"
                  id={`${instanceId}-${group.id}`}
                  m="0"
                  textSize="xs"
                  fontWeight="semibold"
                  color="secondary"
                >
                  {group.title}
                </Box>
              </Box>
              {group.columns.map((field) => {
                const added = selected.has(field.id);
                const locked = added && field.required;
                const copy = (
                  <Box
                    as="span"
                    key={field.id}
                    className={styles.fieldCopy}
                    title={field.help_text ? `${field.title} — ${field.help_text}` : field.title}
                  >
                    {field.title}
                  </Box>
                );
                return locked ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap="2"
                    px="2"
                    py="0"
                    className={styles.fieldRow}
                    color="secondary"
                    textSize="sm"
                    key={field.id}
                    role="group"
                    aria-label={`${field.title} required`}
                    aria-description={field.help_text}
                    title={field.help_text ? `Required field — ${field.help_text}` : 'Required field'}
                  >
                    {copy}
                    <Box
                      as="span"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                      width="20px"
                    >
                      <Icon icon="lock" />
                    </Box>
                  </Box>
                ) : (
                  <Box
                    as="label"
                    key={field.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap="2"
                    px="2"
                    py="0"
                    className={styles.fieldRow}
                    data-active={added}
                    m="0"
                    textSize="sm"
                    fontWeight="regular"
                    color="body"
                    cursor="pointer"
                  >
                    {copy}
                    <Box
                      as="span"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                      width="20px"
                    >
                      <Checkbox
                        size="sm"
                        aria-label={field.title}
                        aria-description={field.help_text}
                        checked={added}
                        disabled={disabled}
                        onChange={() => (added ? remove(field) : add(field))}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
          {visibleGroups.length === 0 && (
            <Box as="p" m="0" p="3" color="secondary" textSize="sm" role="status">
              No fields match “{search}”.
            </Box>
          )}
        </Stack>
      </div>
    </Box>
  );
  const detail = (
    <Box as="section" display="flex" flexDirection="column" minWidth="0" minHeight="0" aria-label="Selected columns">
      {selectedHeader ?? (
        <Box as="h3" textSize="md" m="0" pb="2" flexShrink={0}>
          Selected columns
        </Box>
      )}
      <div ref={scrollRef} className={styles.scrollViewport}>
        <ol className={styles.list} aria-label="Export column order">
          {columns.map((column, index) => {
            const definition = definitions.get(column.field);
            if (!definition) return null;
            return (
              <ColumnRow
                key={column.field}
                {...definition}
                column={column}
                index={index}
                total={columns.length}
                instanceId={instanceId}
                disabled={disabled}
                onRename={(title) =>
                  onChange(columns.map((value, position) => (position === index ? { ...value, title } : value)))
                }
                onRemove={() => remove(definition.field)}
              />
            );
          })}
        </ol>
      </div>
    </Box>
  );
  if (workspace)
    return (
      <BuilderWorkspace sidebar={available} disabled={disabled} {...workspace}>
        {detail}
      </BuilderWorkspace>
    );
  return (
    <Grid gridTemplateColumns={builderColumns} gap="3" height="full" minHeight="0">
      {available}
      {detail}
    </Grid>
  );
}
